import React from "react";
import "@/App.css";
import "@/styles/themes.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import JobsList from "@/pages/Jobs/JobsList";
import JobNew from "@/pages/Jobs/JobNew";
import JobDetail from "@/pages/Jobs/JobDetail";
import Quotations from "@/pages/Quotations";
import Invoices from "@/pages/Invoices";
import ActivityLog from "@/pages/ActivityLog";
import BLPrint from "@/pages/BLPrint";
import InvoicePrint from "@/pages/InvoicePrint";
import SIPrint from "@/pages/docs/SIPrint";
import ManifestPrint from "@/pages/docs/ManifestPrint";
import ArrivalNoticePrint from "@/pages/docs/ArrivalNoticePrint";
import DeliveryOrderPrint from "@/pages/docs/DeliveryOrderPrint";
import DGDeclarationPrint from "@/pages/docs/DGDeclarationPrint";
import SignOffBoard from "@/pages/SignOffBoard";
import SupportInbox from "@/pages/SupportInbox";
import TeamChat from "@/pages/TeamChat";
import Imports from "@/pages/Imports";
import Reports from "@/pages/Reports";
import Documents from "@/pages/Documents";
import RateAgreements from "@/pages/RateAgreements";
import Exports from "@/pages/Exports";
import PendingInvoices from "@/pages/PendingInvoices";
import VendorBills from "@/pages/VendorBills";
import Reimbursements from "@/pages/Reimbursements";
import CarrierLocalCharges from "@/pages/CarrierLocalCharges";
import Settings from "@/pages/Settings";
import TdsRegister from "@/pages/TdsRegister";
import SettingsLayout from "@/pages/Admin/SettingsLayout";
import SettingsUsers from "@/pages/Admin/SettingsUsers";
import SettingsNotifications from "@/pages/Admin/SettingsNotifications";
import SettingsIntegrations from "@/pages/Admin/SettingsIntegrations";
import SettingsAutomation from "@/pages/Admin/SettingsAutomation";
import SettingsMaintenance from "@/pages/Admin/SettingsMaintenance";
import SettingsDanger from "@/pages/Admin/SettingsDanger";
import AdminPermissions from "@/pages/Admin/AdminPermissions";
import AdminBulkDelete from "@/pages/Admin/AdminBulkDelete";
import AdminSiteSettings from "@/pages/Admin/AdminSiteSettings";
import TallySettings from "@/pages/Admin/TallySettings";
import AdminLeads from "@/pages/Admin/AdminLeads";
import CostSheet from "@/pages/CostSheet";
import Variance from "@/pages/Variance";
import VarianceOverview from "@/pages/VarianceOverview";
import Profile from "@/pages/Profile";
import {
  CustomersPage, CarriersPage, PortsPage, EquipmentPage, ChargesPage,
  CompaniesPage, PackageTypesPage, CarrierDGTemplatesPage,
  DepotsPage, CustomsHouseAgentsPage, DGSubstancesPage, LocationsPage,
} from "@/pages/Masters";
import FidelLanding from "@/pages/FidelLanding";
import StatementOfAccount from "@/pages/StatementOfAccount";
import StatementOfAccountPrint from "@/pages/StatementOfAccountPrint";
import ReceiptVoucherPrint from "@/pages/ReceiptVoucherPrint";
import PortalApp from "@/pages/portal/PortalApp";

// Public-only build: when REACT_APP_PUBLIC_ONLY="true", the app ships ONLY the public
// marketing + cargo-tracking site. The /login route and every internal ERP route are
// NOT registered, so they don't exist on the public deployment (any unknown path
// redirects to the landing page). The internal deployment builds without this flag.
const PUBLIC_ONLY = process.env.REACT_APP_PUBLIC_ONLY === "true";

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <Toaster position="top-right" theme="dark" richColors closeButton toastOptions={{ style: { background: "#111318", border: "1px solid #272A35", color: "#fff" } }} />
        <Routes>
          {/* Internal build: staff land straight in the ERP (ProtectedRoute sends
              them to /login when signed out). The marketing site is the front
              door only on the public build. */}
          <Route path="/" element={PUBLIC_ONLY ? <FidelLanding /> : <Navigate to="/dashboard" replace />} />
          {/* Customer Portal — separate auth realm, internet-facing, available in all builds */}
          <Route path="/portal/*" element={<PortalApp />} />
          {!PUBLIC_ONLY && (
          <>
          <Route path="/login" element={<Login />} />

          {/* Print pages — protected but no layout chrome */}
          <Route path="/jobs/:id/bl/print" element={<ProtectedRoute><BLPrint /></ProtectedRoute>} />
          <Route path="/jobs/:id/si/print" element={<ProtectedRoute><SIPrint /></ProtectedRoute>} />
          <Route path="/jobs/:id/manifest/print" element={<ProtectedRoute><ManifestPrint /></ProtectedRoute>} />
          <Route path="/jobs/:id/arrival-notice/print" element={<ProtectedRoute><ArrivalNoticePrint /></ProtectedRoute>} />
          <Route path="/jobs/:id/delivery-order/print" element={<ProtectedRoute><DeliveryOrderPrint /></ProtectedRoute>} />
          <Route path="/jobs/:id/dg-declaration/print" element={<ProtectedRoute><DGDeclarationPrint /></ProtectedRoute>} />
          <Route path="/invoices/:id/print" element={<ProtectedRoute><InvoicePrint /></ProtectedRoute>} />
          <Route path="/reports/soa/print" element={<ProtectedRoute><StatementOfAccountPrint /></ProtectedRoute>} />
          <Route path="/receipts/:paymentId/print" element={<ProtectedRoute><ReceiptVoucherPrint /></ProtectedRoute>} />

          <Route element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/jobs" element={<JobsList />} />
            <Route path="/jobs/new" element={<JobNew />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/imports" element={<Imports />} />
            <Route path="/exports" element={<Exports />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/tds" element={<TdsRegister />} />
            <Route path="/reports/variance" element={<VarianceOverview />} />
            <Route path="/reports/soa" element={<StatementOfAccount />} />
            <Route path="/quotations" element={<Quotations />} />
            <Route path="/rate-agreements" element={<RateAgreements />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/pending-invoices" element={<PendingInvoices />} />
            <Route path="/vendor-bills" element={<VendorBills />} />
            <Route path="/reimbursements" element={<Reimbursements />} />
            <Route path="/signoff" element={<SignOffBoard />} />
            <Route path="/support" element={<SupportInbox />} />
            <Route path="/team-chat" element={<TeamChat />} />
            <Route path="/activity" element={<ActivityLog />} />
            <Route path="/masters/customers" element={<CustomersPage />} />
            <Route path="/masters/carriers" element={<CarriersPage />} />
            <Route path="/masters/ports" element={<PortsPage />} />
            <Route path="/masters/equipment" element={<EquipmentPage />} />
            <Route path="/masters/charges" element={<ChargesPage />} />
            <Route path="/masters/carrier-local-charges" element={<CarrierLocalCharges />} />
            <Route path="/masters/companies" element={<CompaniesPage />} />
            <Route path="/masters/package-types" element={<PackageTypesPage />} />
            <Route path="/masters/carrier-dg-templates" element={<CarrierDGTemplatesPage />} />
            <Route path="/masters/depots" element={<DepotsPage />} />
            <Route path="/masters/customs-house-agents" element={<CustomsHouseAgentsPage />} />
            <Route path="/masters/dg-substances" element={<DGSubstancesPage />} />
            <Route path="/masters/locations" element={<LocationsPage />} />
            <Route element={<SettingsLayout />}>
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/users" element={<SettingsUsers />} />
              <Route path="/settings/notifications" element={<SettingsNotifications />} />
              <Route path="/settings/integrations" element={<SettingsIntegrations />} />
              <Route path="/settings/automation" element={<SettingsAutomation />} />
              <Route path="/settings/maintenance" element={<SettingsMaintenance />} />
              <Route path="/settings/danger" element={<SettingsDanger />} />
              <Route path="/admin/permissions" element={<AdminPermissions />} />
              <Route path="/admin/tally" element={<TallySettings />} />
              <Route path="/admin/bulk-delete" element={<AdminBulkDelete />} />
              <Route path="/admin/site-settings" element={<AdminSiteSettings />} />
            </Route>
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/jobs/:id/cost-sheet" element={<CostSheet />} />
            <Route path="/jobs/:id/variance" element={<Variance />} />
            <Route path="/profile" element={<Profile />} />
          </Route>
          </>
          )}

          <Route path="*" element={<Navigate to={PUBLIC_ONLY ? "/" : "/dashboard"} replace />} />
        </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
